// ── Adapter Supabase do ISessionRepository ────────────────────────
// Única camada que conhece as tabelas de identidade
// (profiles/workspace_members/workspaces/times/time_members).

import { getSupabase } from '@infra/supabase/client';
import type {
  ISessionRepository,
  MemberWorkspaceRow,
  ProfileRow,
  TeamMembershipRow,
  WorkspaceMemberRole,
  WorkspaceRow,
} from '@modules/session';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabaseSessionRepository implements ISessionRepository {
  getProfile = async (userId: string): Promise<ProfileRow | null> => {
    const { data, error } = await client().from('profiles').select('*').eq('id', userId).single();
    if (error) throw error;
    return (data ?? null) as unknown as ProfileRow | null;
  };

  createBasicProfile = async (userId: string): Promise<ProfileRow | null> => {
    const { data, error } = await client()
      .from('profiles')
      .insert({ id: userId, nome: 'Usuário', platform_role: 'member' })
      .select().single();
    if (error) throw error;
    return (data ?? null) as unknown as ProfileRow | null;
  };

  // Marca que o usuário já acessou (some da lista de "não entraram"). Só escreve
  // quando ainda está true para evitar updates desnecessários a cada login.
  markFirstAccessDone = async (userId: string): Promise<void> => {
    const { error } = await client()
      .from('profiles')
      .update({ first_access: false })
      .eq('id', userId)
      .eq('first_access', true);
    if (error) throw error;
  };

  getWorkspaceMemberships = async (userId: string): Promise<WorkspaceMemberRole[]> => {
    const { data, error } = await client()
      .from('workspace_members').select('workspace_id, role')
      .eq('user_id', userId).eq('ativo', true);
    if (error) throw error;
    return (data || []) as unknown as WorkspaceMemberRole[];
  };

  getWorkspace = async (workspaceId: string): Promise<WorkspaceRow | null> => {
    const { data, error } = await client()
      .from('workspaces').select('*').eq('id', workspaceId).eq('ativo', true).single();
    if (error) throw error;
    return (data ?? null) as unknown as WorkspaceRow | null;
  };

  getWorkspaceMemberRole = async (userId: string, workspaceId: string): Promise<string | null> => {
    const { data } = await client()
      .from('workspace_members').select('role')
      .eq('user_id', userId).eq('workspace_id', workspaceId).eq('ativo', true).maybeSingle();
    return (data?.role ?? null) as string | null;
  };

  getTeamMembership = async (userId: string, workspaceId: string): Promise<TeamMembershipRow | null> => {
    const { data, error } = await client()
      .from('time_members').select('time_id, role, times!inner(*)')
      .eq('user_id', userId).eq('ativo', true)
      .eq('times.workspace_id', workspaceId).eq('times.ativo', true);
    if (error) throw error;
    const rows = (data ?? []) as unknown as TeamMembershipRow[];
    if (rows.length === 0) return null;
    // Papel duplo: a pessoa pode ter mais de uma linha no time (ex.: closer + sdr).
    // Escolhe determinísticamente o papel operacional por prioridade — evita o
    // erro de "múltiplas linhas" e mantém comportamento estável.
    const priority: Record<string, number> = { gestor: 0, aex: 1, closer: 2, sdr: 3 };
    rows.sort((a, b) => (priority[a.role] ?? 9) - (priority[b.role] ?? 9));
    return rows[0];
  };

  listTeamMemberIds = async (timeId: string): Promise<string[]> => {
    if (!timeId) return [];
    const { data, error } = await client()
      .from('time_members').select('user_id')
      .eq('time_id', timeId).eq('ativo', true);
    if (error) throw error;
    return ((data || []) as Array<{ user_id: string | null }>)
      .map(r => r.user_id)
      .filter((id): id is string => !!id);
  };

  listAllActiveWorkspaces = async (): Promise<WorkspaceRow[]> => {
    const { data, error } = await client().from('workspaces').select('*').eq('ativo', true).order('name');
    if (error) throw error;
    return (data || []) as unknown as WorkspaceRow[];
  };

  listMemberWorkspaces = async (userId: string): Promise<MemberWorkspaceRow[]> => {
    const { data, error } = await client()
      .from('workspace_members').select('workspace_id, role, workspaces(*)')
      .eq('user_id', userId).eq('ativo', true);
    if (error) throw error;
    return (data || []) as unknown as MemberWorkspaceRow[];
  };
}
